import { z } from "zod";
import { buildAuthJsonHeaders } from "../lib/auth-headers";
import { getRuntimeApiBase } from "../lib/runtime-api-base";
import {
  emptyLockoutState,
  type DeskPlanSchedule,
  type LockoutPolicy,
  type LockoutState,
} from "./lockout-types";

const LockoutPolicySchema = z.object({
  lockoutEnabled: z.boolean(),
  autoLockFromDeskPlan: z.boolean(),
  autoReleaseMinutes: z.number().int().min(0).max(120),
});

const DeskPlanScheduleSchema = z.object({
  status: z.enum([
    "scheduled",
    "active",
    "disabled",
    "unavailable",
    "stale",
    "invalid",
    "dependency_unavailable",
  ]),
  timezone: z.literal("America/New_York"),
  detail: z.string(),
  planDate: z.string().nullable(),
  planGeneratedAt: z.string().nullable(),
  windowStartAt: z.string().nullable(),
  windowEndAt: z.string().nullable(),
  releaseAt: z.string().nullable(),
  eventName: z.string().nullable(),
});

const LockoutResponseSchema = z.object({
  locked: z.boolean().default(false),
  until: z.string().nullable().default(null),
  remaining: z.number().nullable().default(null),
  autoReleaseAt: z.string().nullable().optional(),
  scheduledBy: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
  policy: LockoutPolicySchema.optional(),
  schedule: DeskPlanScheduleSchema.optional(),
});

interface TransportResult {
  httpOk: boolean;
  body: Record<string, unknown>;
  reason: string | null;
}

export async function fetchLockoutStatus(): Promise<LockoutState> {
  const result = await requestLockout("/status");
  if (!result.httpOk) return emptyLockoutState(result.reason);
  return parseLockoutState(result, true);
}

export async function fetchLockoutSchedule(): Promise<LockoutState> {
  const result = await requestLockout("/next-window");
  if (!result.httpOk) return emptyLockoutState(result.reason);
  return parseLockoutState(result, true);
}

export async function updateLockoutPolicy(input: {
  enabled: boolean;
  autoReleaseMinutes: number;
}): Promise<LockoutState> {
  const result = await requestLockout("/policy", {
    method: "PUT",
    body: JSON.stringify(input),
  });
  if (!result.httpOk) return emptyLockoutState(result.reason);
  const nestedState = isRecord(result.body.state) ? result.body.state : {};
  return parseLockoutState(
    {
      ...result,
      body: { ...nestedState, policy: result.body.policy },
    },
    true,
  );
}

export async function requestLockoutToggle(input: {
  locked: boolean;
  durationMinutes?: number;
  briefingAnchor?: string;
}): Promise<LockoutState> {
  return commandRequest("/toggle", input);
}

export async function requestLockoutSchedule(input: {
  durationMinutes?: number;
  lockUntil?: string;
  windowStartTime?: string;
}): Promise<LockoutState> {
  return commandRequest("/schedule", input);
}

export async function requestDeskPlanLockout(): Promise<LockoutState> {
  return commandRequest("/lock-until-desk-session", {});
}

async function commandRequest(
  path: string,
  body: Record<string, unknown>,
): Promise<LockoutState> {
  const result = await requestLockout(path, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return parseLockoutState(result, false);
}

function parseLockoutState(
  result: TransportResult,
  requiresPolicy: boolean,
): LockoutState {
  const parsed = LockoutResponseSchema.safeParse(result.body);
  if (!parsed.success)
    return emptyLockoutState("Lockout returned an invalid response.");
  const policy = parsed.data.policy ?? null;
  return {
    locked: parsed.data.locked,
    until: parsed.data.until,
    remaining: parsed.data.remaining,
    autoReleaseAt: parsed.data.autoReleaseAt ?? null,
    scheduledBy: parsed.data.scheduledBy ?? null,
    reason: parsed.data.reason ?? null,
    policy,
    policyResolved: requiresPolicy ? policy !== null : true,
    schedule: (parsed.data.schedule as DeskPlanSchedule | undefined) ?? null,
    commandOk: result.httpOk && result.body.ok !== false,
    error: result.httpOk && result.body.ok !== false ? null : result.reason,
  };
}

async function requestLockout(
  path: string,
  init: RequestInit = {},
): Promise<TransportResult> {
  try {
    const headers = await buildAuthJsonHeaders();
    const response = await fetch(`${getRuntimeApiBase()}/api/lockout${path}`, {
      ...init,
      credentials: "include",
      headers: { ...headers, ...init.headers },
    });
    const value: unknown = await response.json().catch(() => ({}));
    const body = isRecord(value) ? value : {};
    return {
      httpOk: response.ok,
      body,
      reason:
        typeof body.reason === "string"
          ? body.reason
          : response.ok
            ? null
            : `Lockout request failed (${response.status}).`,
    };
  } catch (error) {
    return {
      httpOk: false,
      body: {},
      reason:
        error instanceof Error ? error.message : "Lockout service unavailable.",
    };
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

export type { LockoutPolicy };
